import { useState } from 'react';
import Button from './Button';
import Modal from './Modal';

interface FileItem {
  id: string;
  fileName: string;
  originalName?: string;
  mimeType: string;
  size: number;
  url: string;
  thumbnailUrl?: string;
  createdAt?: string;
  uploadedBy?: string;
}

interface FileListProps {
  files: FileItem[];
  onDelete?: (file: FileItem) => void;
  onDownload?: (file: FileItem) => void;
  showPreview?: boolean;
  showDelete?: boolean;
  showDownload?: boolean;
  layout?: 'list' | 'grid';
  loading?: boolean;
  emptyMessage?: string;
  className?: string;
}

const formatFileSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDate = (dateString?: string) => {
  if (!dateString) return '';
  return new Date(dateString).toLocaleDateString('ja-JP', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getFileIcon = (mimeType: string, size: 'sm' | 'md' | 'lg' = 'md') => {
  const sizeClasses = {
    sm: 'w-6 h-6',
    md: 'w-8 h-8',
    lg: 'w-12 h-12',
  };

  if (mimeType.startsWith('image/')) {
    return (
      <svg className={`${sizeClasses[size]} text-purple-500`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    );
  }
  if (mimeType === 'application/pdf') {
    return (
      <svg className={`${sizeClasses[size]} text-red-500`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
      </svg>
    );
  }
  if (mimeType.includes('spreadsheet') || mimeType.includes('excel')) {
    return (
      <svg className={`${sizeClasses[size]} text-green-500`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
      </svg>
    );
  }
  if (mimeType.includes('word') || mimeType.includes('document')) {
    return (
      <svg className={`${sizeClasses[size]} text-blue-500`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    );
  }
  return (
    <svg className={`${sizeClasses[size]} text-gray-500`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  );
};

export default function FileList({
  files,
  onDelete,
  onDownload,
  showPreview = true,
  showDelete = true,
  showDownload = true,
  layout = 'list',
  loading = false,
  emptyMessage = '添付ファイルはありません',
  className = '',
}: FileListProps) {
  const [previewFile, setPreviewFile] = useState<FileItem | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<FileItem | null>(null);

  // filesがnull/undefined/配列でない場合は空配列として扱う
  const safeFiles = Array.isArray(files) ? files : [];

  const handleDownload = async (file: FileItem) => {
    if (onDownload) {
      onDownload(file);
    } else {
      // デフォルトのダウンロード処理
      try {
        const response = await fetch(file.url);
        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = file.originalName || file.fileName || 'unknown';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      } catch (error) {
        console.error('Download failed:', error);
        window.open(file.url, '_blank');
      }
    }
  };

  const handleDelete = () => {
    if (deleteConfirm && onDelete) {
      onDelete(deleteConfirm);
      setDeleteConfirm(null);
    }
  };

  const isImageFile = (mimeType: string) => mimeType.startsWith('image/');
  const isPdfFile = (mimeType: string) => mimeType === 'application/pdf';

  if (loading) {
    return (
      <div className={`flex justify-center items-center py-8 ${className}`}>
        <div className="w-6 h-6 border-2 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
      </div>
    );
  }

  if (safeFiles.length === 0) {
    return (
      <div className={`text-center py-8 text-gray-500 dark:text-gray-400 ${className}`}>
        {emptyMessage}
      </div>
    );
  }

  return (
    <>
      {layout === 'grid' ? (
        <div className={`grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 ${className}`}>
          {safeFiles.map((file) => (
            <div
              key={file.id}
              className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              {/* サムネイル or アイコン */}
              <div
                className="aspect-square mb-2 flex items-center justify-center bg-gray-100 dark:bg-gray-900 rounded-lg cursor-pointer overflow-hidden"
                onClick={() => showPreview && setPreviewFile(file)}
              >
                {isImageFile(file.mimeType) && (file.thumbnailUrl || file.url) ? (
                  <img
                    src={file.thumbnailUrl || file.url}
                    alt={file.originalName || file.fileName || 'unknown'}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  getFileIcon(file.mimeType, 'lg')
                )}
              </div>

              {/* ファイル名 */}
              <p className="text-sm font-medium text-gray-900 dark:text-white truncate mb-1">
                {file.originalName || file.fileName || 'unknown'}
              </p>
              <p className="text-xs text-gray-500">{formatFileSize(file.size)}</p>

              {/* アクション */}
              <div className="flex gap-1 mt-2">
                {showDownload && (
                  <button
                    onClick={() => handleDownload(file)}
                    className="p-1 text-blue-600 hover:bg-blue-100 dark:hover:bg-blue-900/50 rounded"
                    title="ダウンロード"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                  </button>
                )}
                {showDelete && onDelete && (
                  <button
                    onClick={() => setDeleteConfirm(file)}
                    className="p-1 text-red-500 hover:bg-red-100 dark:hover:bg-red-900/50 rounded"
                    title="削除"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className={`space-y-2 ${className}`}>
          {safeFiles.map((file) => (
            <div
              key={file.id}
              className="flex items-center gap-3 p-3 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              {/* サムネイル or アイコン */}
              <div
                className="w-12 h-12 flex-shrink-0 flex items-center justify-center bg-gray-100 dark:bg-gray-900 rounded-lg cursor-pointer overflow-hidden"
                onClick={() => showPreview && setPreviewFile(file)}
              >
                {isImageFile(file.mimeType) && (file.thumbnailUrl || file.url) ? (
                  <img
                    src={file.thumbnailUrl || file.url}
                    alt={file.originalName || file.fileName || 'unknown'}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  getFileIcon(file.mimeType)
                )}
              </div>

              {/* ファイル情報 */}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                  {file.originalName || file.fileName || 'unknown'}
                </p>
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <span>{formatFileSize(file.size)}</span>
                  {file.createdAt && (
                    <>
                      <span>•</span>
                      <span>{formatDate(file.createdAt)}</span>
                    </>
                  )}
                  {file.uploadedBy && (
                    <>
                      <span>•</span>
                      <span>{file.uploadedBy}</span>
                    </>
                  )}
                </div>
              </div>

              {/* アクション */}
              <div className="flex items-center gap-1">
                {showPreview && (isImageFile(file.mimeType) || isPdfFile(file.mimeType)) && (
                  <button
                    onClick={() => setPreviewFile(file)}
                    className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                    title="プレビュー"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  </button>
                )}
                {showDownload && (
                  <button
                    onClick={() => handleDownload(file)}
                    className="p-2 text-blue-600 hover:bg-blue-100 dark:hover:bg-blue-900/50 rounded"
                    title="ダウンロード"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                  </button>
                )}
                {showDelete && onDelete && (
                  <button
                    onClick={() => setDeleteConfirm(file)}
                    className="p-2 text-red-500 hover:bg-red-100 dark:hover:bg-red-900/50 rounded"
                    title="削除"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* プレビューモーダル */}
      <Modal
        isOpen={!!previewFile}
        onClose={() => setPreviewFile(null)}
        title={previewFile?.originalName || previewFile?.fileName || 'プレビュー'}
        size="xl"
      >
        {previewFile && (
          <div className="space-y-4">
            <div className="bg-gray-100 dark:bg-gray-900 rounded-lg overflow-hidden">
              {isImageFile(previewFile.mimeType) ? (
                <img
                  src={previewFile.url}
                  alt={previewFile.originalName || previewFile.fileName || 'unknown'}
                  className="w-full max-h-[70vh] object-contain"
                />
              ) : isPdfFile(previewFile.mimeType) ? (
                <iframe
                  src={previewFile.url}
                  className="w-full h-[70vh]"
                  title={previewFile.originalName || previewFile.fileName || 'unknown'}
                />
              ) : (
                <div className="flex flex-col items-center justify-center py-12">
                  {getFileIcon(previewFile.mimeType, 'lg')}
                  <p className="mt-4 text-gray-500">このファイル形式はプレビューできません</p>
                </div>
              )}
            </div>
            <div className="flex justify-between items-center">
              <div className="text-sm text-gray-500">
                <span>{formatFileSize(previewFile.size)}</span>
                {previewFile.createdAt && (
                  <>
                    <span className="mx-2">•</span>
                    <span>{formatDate(previewFile.createdAt)}</span>
                  </>
                )}
              </div>
              <Button onClick={() => handleDownload(previewFile)}>
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                ダウンロード
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* 削除確認モーダル */}
      <Modal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title="ファイルを削除"
        size="sm"
      >
        {deleteConfirm && (
          <div className="space-y-4">
            <p className="text-gray-700 dark:text-gray-300">
              「{deleteConfirm.originalName || deleteConfirm.fileName || 'unknown'}」を削除しますか？
              <br />
              <span className="text-sm text-gray-500">この操作は取り消せません。</span>
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setDeleteConfirm(null)}>
                キャンセル
              </Button>
              <Button variant="danger" onClick={handleDelete}>
                削除
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
}
